import React, {forwardRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, createSlotRecipeContext, RadioGroup, Text} from '@chakra-ui/react3'
import productTileRecipe from './leap-view.recipe'
import DisImage from '../../../leap/components/dis-image'
import PropTypes from 'prop-types'
import {ColorSwatchItem} from '../../../leap/components/color-swatches'
import {CloseIcon, UnavailableCartIcon} from '../../icons'
import {MultiColorIcon} from '../../../leap/components/icons'

const {withContext, withProvider, useStyles, useClassNames} = createSlotRecipeContext({
    recipe: productTileRecipe
})

const ProductTileRootImpl = forwardRef((props, ref) => {
    const {formatMessage} = useIntl()
    return (
        <Box
            ref={ref}
            data-testid="product-tile"
            role="group"
            aria-label={formatMessage({
                id: 'global.product',
                defaultMessage: 'Product'
            })}
            {...props}
        />
    )
})
if (process.env.NODE_ENV !== 'production') {
    ProductTileRootImpl.displayName = 'ProductTileRoot'
}
const ProductTileRoot = withProvider(ProductTileRootImpl, 'root')

const ProductTileContainer = withContext('div', 'container')

const ProductTileImagesImpl = forwardRef(({images, widths, reverseImageOrder, ...rest}, ref) => {
    const styles = useStyles()
    const [defaultImage, hoveredImage] =
        reverseImageOrder && images.length > 1 ? [images[1], images[0]] : [images[0], images[1]]
    return (
        <Box ref={ref} {...rest}>
            {images.length > 0 && (
                <DisImage
                    css={styles.image}
                    {...(images.length > 1 && {
                        'data-not-hovered': 'container'
                    })}
                    src={defaultImage.disBaseLink || defaultImage.link}
                    widths={widths}
                    alt={defaultImage.alt}
                    loading="lazy"
                />
            )}
            {images.length > 1 && (
                <DisImage
                    css={styles.image}
                    data-hovered="container"
                    src={hoveredImage.disBaseLink || hoveredImage.link}
                    widths={widths}
                    alt={hoveredImage.alt}
                    loading="lazy"
                />
            )}
        </Box>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ProductTileImagesImpl.displayName = 'ProductTileImages'
}
ProductTileImagesImpl.propTypes = {
    images: PropTypes.array,
    widths: PropTypes.object,
    reverseImageOrder: PropTypes.bool
}
const ProductTileImages = withContext(ProductTileImagesImpl, 'imagesContainer')

const ProductTileBadges = withContext('div', 'badgesContainer')

const ProductTileUnavailableImpl = forwardRef((props, ref) => {
    const styles = useStyles()
    return (
        <Box ref={ref} {...props}>
            <Box css={styles.unavailableIconBox}>
                <UnavailableCartIcon boxSize={6} />
                <Box css={styles.unavailableCloseIcon}>
                    <CloseIcon color="white" boxSize={1.5} />
                </Box>
            </Box>
            <Text css={styles.unavailableText}>
                <FormattedMessage
                    defaultMessage="This product is no longer available."
                    id="product_tile.discontinued"
                />
            </Text>
        </Box>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ProductTileUnavailableImpl.displayName = 'ProductTileUnavailable'
}
const ProductTileUnavailable = withContext(ProductTileUnavailableImpl, 'unavailableContainer')

const ProductTileProductInfo = withContext('div', 'productInfoContainer')

const ProductTileProductDetails = withContext('div', 'productDetailsContainer')

const ProductTileProductName = withContext('p', 'productName', {
    defaultProps: {
        lineClamp: 2
    }
})

const ProductTileSubline = withContext('p', 'subline')

const ProductTileCustomizeImpl = forwardRef((props, ref) => {
    const styles = useStyles()
    return (
        <Box ref={ref} {...props}>
            <MultiColorIcon />
            <Text css={styles.customizeText} data-testid="producttile-customize">
                <FormattedMessage defaultMessage="Customise" id="product_tile.customize" />
            </Text>
        </Box>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ProductTileCustomizeImpl.displayName = 'ProductTileCustomize'
}
const ProductTileCustomize = withContext(ProductTileCustomizeImpl, 'customizeContainer')

const ProductTilePrice = withContext('div', 'priceContainer')

const ProductTilePayLater = withContext('div', 'payLaterContainer')

const ProductTileSwatches = withContext(RadioGroup.Root, 'swatchesContainer', {
    defaultProps: {
        orientation: 'horizontal'
    }
})

const ProductTileSwatch = withContext(ColorSwatchItem, 'swatch', {
    defaultProps: {
        size: '18'
    }
})

const ProductTileSwatchesMoreButton = forwardRef(({css, className, count, ...props}, ref) => {
    const classNames = useClassNames()
    const styles = useStyles()

    // count is required prop
    if (count == null) {
        throw new Error(
            '"<ProductTileSwatchesMoreButton />" requires a "count" prop. ' +
                'Please provide a value for the "count" prop.'
        )
    }

    if (count <= 4) {
        return null
    }
    return (
        <Box
            as="button"
            ref={ref}
            {...props}
            className={`${classNames.swatchMoreButton} ${className}`}
            css={[
                styles.swatchMoreButton,
                count > 8 ? styles.swatchMoreDesktop : styles.swatchMoreMobile,
                css
            ]}
        >
            {count > 4 && (
                <Box as="span" css={styles.swatchMoreMobileLabel}>
                    <span className="sr-only">
                        <FormattedMessage
                            id="leap.product-colors.plus.assistive_msg"
                            defaultMessage="{count, plural, one {# additional colour} other {# additional colours}}"
                            values={{count: count - 4}}
                        />
                    </span>
                    <span aria-hidden>+{count - 4}</span>
                </Box>
            )}
            {count > 8 && (
                <Box as="span" css={styles.swatchMoreDesktopLabel}>
                    <span className="sr-only">
                        <FormattedMessage
                            id="leap.product-colors.plus.assistive_msg"
                            defaultMessage="{count, plural, one {# additional colour} other {# additional colours}}"
                            values={{count: count - 8}}
                        />
                    </span>
                    <span aria-hidden>+{count - 8}</span>
                </Box>
            )}
        </Box>
    )
})
if (process.env.NODE_ENV !== 'production') {
    ProductTileSwatchesMoreButton.displayName = 'ProductTileSwatchesMoreButton'
}
ProductTileSwatchesMoreButton.propTypes = {
    className: PropTypes.string,
    count: PropTypes.number.isRequired,
    css: PropTypes.any
}

const ProductTileOverlayActionContainer = withContext('div', 'overlayActionContainer')

const ProductTileLeapView = {
    Root: ProductTileRoot,
    Container: ProductTileContainer,
    Images: ProductTileImages,
    Unavailable: ProductTileUnavailable,
    Badges: ProductTileBadges,
    ProductInfo: ProductTileProductInfo,
    ProductDetails: ProductTileProductDetails,
    ProductName: ProductTileProductName,
    Subline: ProductTileSubline,
    Customize: ProductTileCustomize,
    Price: ProductTilePrice,
    PayLater: ProductTilePayLater,
    Swatches: ProductTileSwatches,
    Swatch: ProductTileSwatch,
    SwatchesMoreButton: ProductTileSwatchesMoreButton,
    OverlayActionContainer: ProductTileOverlayActionContainer
}

export default ProductTileLeapView
